import * as React from "react";
import i18n from "../../i18n/i18n";
import StepGuideSidebar from "./StepGuideSidebarComponent";
import StepGuideContent from "./StepGuideContentComponent";
import { ApplicationSubmitStatusType } from "../../models/enums/ApplicationSubmitStatusType";
import classNames from "classnames";
import Card from "../DataDisplay/CardComponent";
import IApplicationForTaxChangeVm from "../../models/citizen/IApplicationForTaxChangeVm";
import IApplicationForTaxIssuingVm from "../../models/citizen/IApplicationForTaxIssuingVm";
import IApplicationForTaxRenewalVm from "../../models/citizen/IApplicationForTaxRenewalVm";
import IApplicationForTaxReplacementVm from "../../models/citizen/IApplicationForTaxReplacementVm";
import IApplicationForVubReplacementVm from "../../models/citizen/IApplicationForVubReplacementVm";
import IApplicationForCubReplacementVm from "../../models/citizen/IApplicationForCubReplacementVm";
import IApplicationForStaReplacementVm from "../../models/citizen/IApplicationForStaReplacementVm";
import IApplicationForAdrReplacementVm from "../../models/citizen/IApplicationForAdrReplacementVm";
import { CertificateProcessType } from "../../models/flags/CertificateProcessType";
import { CertificateType } from "../../models/flags/CertificateType";
import SummaryComponent from "./SummaryComponent";

export enum StepType {
  Step = 0,
  Confirmation = 1,
  Receipt = 2,
  Resend = 3,
  ResendConfirmation = 4,
}

export interface IStep {
  id: number;
  title: string;
  shortDescription?: string;
  longDescription?: JSX.Element;
  content?: JSX.Element;
  contentSummary?: IContentSummary[];
  completed?: boolean;
  type: StepType;
  applicationSubmitStatusType?: ApplicationSubmitStatusType;
  uiTestAttribute?: string;
}

export interface IContentSummary {
  key: string;
  value: JSX.Element;
}

type IApplicationWithReceipts = IApplicationForTaxChangeVm | IApplicationForTaxIssuingVm | IApplicationForTaxRenewalVm | IApplicationForTaxReplacementVm | IApplicationForVubReplacementVm | IApplicationForCubReplacementVm | IApplicationForStaReplacementVm | IApplicationForAdrReplacementVm;

interface IStepGuideComponentProps {
  application: IApplicationWithReceipts;
  title: string;
  certificateNumber: string;
  steps: IStep[];
  currentStepNumber: number;
  submittedByCitizen: ApplicationSubmitStatusType;
  updateStep: (stepNumber: number) => void;
  onSave: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  isNextOrPreviousDisabled: boolean;
  isAsyncRunning: boolean;
}

export default function StepGuideComponent({ application, title, certificateNumber, steps, currentStepNumber, submittedByCitizen, updateStep, onSave, onSubmit, canSubmit, isNextOrPreviousDisabled, isAsyncRunning }: IStepGuideComponentProps) {
  const currentStep = getCurrentStep(steps, currentStepNumber);
  const stepsMissing = steps.filter((step: IStep) => (step.type === StepType.Step || step.type === StepType.Resend) && !step.completed);
  const stepsCompleted = steps.filter((step: IStep) => (step.type === StepType.Step || step.type === StepType.Resend) && step.completed);
  const classes = classNames({ "bat-hidden": !certificateNumber as any });
  const isTaxReplacement = isIApplicationForTaxReplacementVm(application);
  const isPreviousStepBlocked = currentStepNumber !== steps[0].id && submittedByCitizen !== ApplicationSubmitStatusType.Submitted;
  const isNextStepAvailable = currentStep.type === StepType.Step || currentStep.type === StepType.Resend;
  const isSubmittable = (currentStep.type === StepType.Confirmation || currentStep.type === StepType.ResendConfirmation) && submittedByCitizen !== ApplicationSubmitStatusType.Submitted;
  const [declarationOfLossChecked, setDeclarationOfLossChecked] = React.useState(false);

  function saveAndUpdateStep(stepNumber: number) {
    if (submittedByCitizen === ApplicationSubmitStatusType.NotSubmitted || submittedByCitizen === ApplicationSubmitStatusType.NeedsResubmittal) {
      onSave();
    }
    updateStep(stepNumber);
  }

  function nextStep() {
    changeStep(currentStepNumber + 1);
  }

  function previousStep() {
    changeStep(currentStepNumber - 1);
  }

  function changeStep(step: number) {
    onSave();
    updateStep(step);
  }

  function getCurrentStep(steps: IStep[], currentStepNumber: number): IStep {
    // If application has been submitted, the list of steps only has the Receipt step.
    // But if currentStepNumber is not 0, we awlays update the current step to be 0, and force the user to land on the receipt step.
    if (steps.length === 1 && steps[0].type === StepType.Receipt && currentStepNumber !== 0) {
      saveAndUpdateStep(0);
      return steps[0];
    } else {
      return steps[currentStepNumber];
    }
  }

  function isIApplicationForTaxReplacementVm(application: IApplicationWithReceipts): application is IApplicationForTaxReplacementVm {
    if (application.certificateType === CertificateType.Tax && (application.certificateProcessType == CertificateProcessType.Replacement || application.certificateProcessType === CertificateProcessType.ReplacementBasedOnExternal))
      {
        return true;
      }
    return false;
  }

  return (
    <div className="row-fluid">
      <div className="span3">
        <StepGuideSidebar steps={steps} currentStepNumber={currentStepNumber} submittedByCitizen={submittedByCitizen} onClickStep={(stepNumber) => saveAndUpdateStep(stepNumber)} isOnClickDisabled={isNextOrPreviousDisabled && !canSubmit} isAsyncRunning={isAsyncRunning} />
      </div>
      <div className="span9">
        <h2>{title}</h2>
        <div className="row-fluid">
          <span className={classes} data-ui-test="application-number">
            {i18n.t("TEXT_APPLICATION_NUMBER")}: {certificateNumber}
          </span>
        </div>
        <br />
        <div>
          {(currentStep.type === StepType.Step || currentStep.type === StepType.Resend || currentStep.type === StepType.Receipt) && <StepGuideContent steps={steps} currentStepNumber={currentStepNumber} />}
          {(currentStep.type === StepType.Confirmation || currentStep.type === StepType.ResendConfirmation) && (
            <Card key={currentStepNumber} title={i18n.t("TEXT_CONFIRM")}>
              {stepsMissing.length > 0 && (submittedByCitizen === ApplicationSubmitStatusType.NotSubmitted || submittedByCitizen === ApplicationSubmitStatusType.NeedsResubmittal) && (
                <div data-ui-test="confirm-missing-container" className="alert alert-warning">
                  <h3 data-ui-test="confirm-missing">{i18n.t("TEXT_STEPS_MISSING")}</h3>
                  <ul className="list">
                    {stepsMissing.map((step: IStep) => {
                      return (
                        <li key={step.id}>
                          <a className="clickable" onClick={() => updateStep(step.id)}>
                            {step.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {stepsCompleted.length > 0 && (submittedByCitizen === ApplicationSubmitStatusType.NotSubmitted || submittedByCitizen === ApplicationSubmitStatusType.NeedsResubmittal) && (
                <div data-ui-test="confirm-completed-container" className="alert alert-info">
                  <h3 data-ui-test="confirm-completed">{i18n.t("TEXT_STEPS_COMPLETED")}</h3>
                  <ul className="list">
                    {stepsCompleted.map((step: IStep) => {
                      return (
                        <li key={step.id}>
                          <a className="clickable" onClick={() => saveAndUpdateStep(step.id)}>
                            {step.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {stepsMissing.length === 0 && (
                <div>
                  <div className="row-fluid">Bekræft dine oplysninger ved at indsende din ansøgning.</div>
                  {submittedByCitizen === ApplicationSubmitStatusType.Submitted && <div className="row-fluid">{i18n.t("TEXT_INFORMATION_CONFIRMED")}: </div>}
                  <br />
                  <div className="row-fluid">{isIApplicationForTaxReplacementVm(application) ? <SummaryComponent application={application} stepsCompleted={stepsCompleted} declarationOfLossChecked={declarationOfLossChecked} setDeclarationOfLossChecked={setDeclarationOfLossChecked} /> : <SummaryComponent stepsCompleted={stepsCompleted} declarationOfLossChecked={declarationOfLossChecked} setDeclarationOfLossChecked={setDeclarationOfLossChecked} />}</div>
                </div>
              )}
            </Card>
          )}
        </div>
        <div className="well well-small clearfix">
          {!isPreviousStepBlocked && (
            <button disabled={isNextOrPreviousDisabled} className="btn pull-left" onClick={() => previousStep()} data-ui-test="step-guide-prev-btn">
              <i className="icon-chevron-left"></i> Forrige <span className="accessibility">trin</span>
            </button>
          )}
          {isNextStepAvailable && (
            <button disabled={isNextOrPreviousDisabled} className="btn btn-oim pull-right" onClick={() => nextStep()} data-ui-test="step-guide-next-btn">
              Næste <span className="accessibility">trin</span>
              <i className="icon-chevron-right"></i>
            </button>
          )}
          {isSubmittable && (
            <button disabled={isAsyncRunning || !canSubmit || (isTaxReplacement === true && !declarationOfLossChecked)} className="btn btn-oim pull-right" onClick={() => onSubmit()} data-ui-test="step-guide-submit-btn">
              Indsend <i className="icon-chevron-right"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
